class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.busy = false;
    this.pendingChanges = new Map();
    this.inFlight = false;
    this.addEventListener('click', (event) => {
      const close = event.target.closest('[data-cart-close], #CartDrawer-Overlay');
      if (close) return this.close();
      const control = event.target.closest('[data-cart-step], [data-cart-remove]');
      if (control && !control.disabled) this.changeGroup(control);
    });
    this.addEventListener('keydown', (event) => {
      if (this.classList.contains('vc-page')) return;
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      if (event.key !== 'Tab' || !this.classList.contains('active')) return;
      const focusable = [...this.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]')].filter(e => e.getClientRects().length && !e.hidden && !e.closest('[inert]'));
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (!this.contains(document.activeElement)) { event.preventDefault(); first?.focus(); }
      else if (event.shiftKey && (document.activeElement === first || document.activeElement === this.querySelector('.drawer__inner'))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    this.addEventListener('submit', (event) => {
      if (this.busy || !this.querySelector('.vc-item')) event.preventDefault();
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('#cart-icon-bubble');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    });
    this.setHeaderCartIconAccessibility();
    this.syncCheckout();
    window.addEventListener('pageshow', event => {
      if (event.persisted || performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.refreshFromServer();
    });
  }
  async refreshFromServer() {
    if (this.busy || this.refreshing) return;
    this.refreshing = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const sections = await this.jsonFetch(root + '?sections=cart-drawer,cart-icon-bubble', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!this.busy) this.renderContents({ sections }, false);
    } catch (error) {
      const status = this.querySelector('[data-cart-status]');
      if (status) status.textContent = 'לא ניתן לרענן כרגע את העגלה. רעננו את הדף לבדיקת מצב הסל.';
    } finally { this.refreshing = false; }
  }
  syncCheckout() {
    const checkout = this.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.disabled = this.busy || !this.querySelector('.vc-item');
  }
  setHeaderCartIconAccessibility() {
    const icon = document.getElementById('cart-icon-bubble');
    if (icon) { icon.setAttribute('aria-haspopup', 'dialog'); icon.setAttribute('aria-controls', 'CartDrawer'); }
  }
  setActiveElement(element) { if (element) this.activeElement = element; }
  open(trigger) {
    if (trigger) this.refreshFromServer();
    if (this.classList.contains('vc-page')) { this.scrollIntoView({block: 'start'}); return; }
    if (trigger) this.setActiveElement(trigger);
    if (this.classList.contains('active')) return;
    this.setActiveElement(this.activeElement || document.activeElement);
    this.classList.add('animate', 'active');
    this.querySelector('.drawer__inner')?.removeAttribute('inert');
    document.body.classList.add('overflow-hidden');
    this.background = [...document.body.children].filter(e => e !== this && !e.contains(this) && !['SCRIPT', 'STYLE', 'LINK'].includes(e.tagName));
    this.backgroundState = this.background.map(e => e.inert);
    this.background.forEach(e => { e.inert = true; });
    const focusInside = () => { if (this.classList.contains('active')) this.querySelector('[data-cart-close]')?.focus({ preventScroll: true }); };
    requestAnimationFrame(() => requestAnimationFrame(focusInside));
    this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) focusInside(); }, 240);
  }
  close() {
    if (this.classList.contains('vc-page')) return;
    if (!this.classList.contains('active')) return;
    clearTimeout(this.focusTimer);
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.background?.forEach((e, i) => { e.inert = this.backgroundState[i]; });
    this.background = null;
    this.querySelector('.drawer__inner')?.setAttribute('inert', '');
    if (this.activeElement?.isConnected) this.activeElement.focus({ preventScroll: true });
  }
  getSectionsToRender() { return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }]; }
  getSectionInnerHTML(html, selector = '.shopify-section') { return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML; }
  renderContents(state, open = true) {
    const html = state.sections?.['cart-drawer'];
    const fragment = html && new DOMParser().parseFromString(html, 'text/html');
    const inner = fragment?.querySelector('#CartDrawer');
    if (!inner) throw new Error('לא ניתן לרענן את העגלה. רעננו את הדף כדי לראות את המצב העדכני.');
    this.querySelector('#CartDrawer').innerHTML = inner.innerHTML;
    if (this.classList.contains('vc-page')) { const panel=this.querySelector('.drawer__inner'); panel.setAttribute('role','region'); panel.removeAttribute('aria-modal'); }
    this.classList.toggle('is-empty', !!fragment.querySelector('cart-drawer.is-empty'));
    if (state.sections['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      const content = this.getSectionInnerHTML(state.sections['cart-icon-bubble']);
      if (icon && content != null) icon.innerHTML = content;
    }
    this.setHeaderCartIconAccessibility();
    this.syncCheckout();
    this.querySelector('.drawer__inner')?.toggleAttribute('inert', !this.classList.contains('vc-page') && !this.classList.contains('active') && !open);
    if (open) this.open();
  }
  async jsonFetch(url, options) {
    const response = await fetch(url, options);
    if (!/(?:json|javascript)/i.test(response.headers.get('content-type') || '')) throw new Error(response.status === 429 ? 'החיבור עמוס כרגע. המתינו מעט ונסו שוב.' : 'לא ניתן לעדכן כרגע את העגלה. נסו שוב.');
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'לא ניתן לעדכן את הכמות שביקשתם.');
    return data;
  }
  groupElement(id) { return [...this.querySelectorAll('[data-cart-group]')].find(group => group.dataset.cartGroup === id); }
  setBusy() {
    const wasBusy = this.busy;
    this.busy = this.inFlight || this.pendingChanges.size > 0;
    this.toggleAttribute('aria-busy', this.busy);
    if (this.busy) this.setAttribute('aria-busy', 'true');
    this.syncCheckout();
    if (wasBusy && !this.busy) this.dispatchEvent(new Event('vonela:cart-idle'));
  }
  whenIdle() {
    return this.busy ? new Promise(resolve => this.addEventListener('vonela:cart-idle', resolve, { once: true })) : Promise.resolve();
  }
  paintDesired(group, quantity) {
    group.dataset.desiredQuantity = String(quantity);
    group.toggleAttribute('data-pending-remove', quantity === 0);
    group.querySelector('.vc-quantity output').textContent = String(quantity);
    group.querySelectorAll('[data-cart-step], [data-cart-remove]').forEach(button => {
      button.disabled = quantity === 0 || (button.dataset.cartStep === '-1' && quantity <= 1);
    });
  }
  changeGroup(control) {
    const group = control.closest('[data-cart-group]'), id = group?.dataset.cartGroup;
    if (!id) return;
    if (!this.busy) this.confirmedMarkup = this.querySelector('#CartDrawer').innerHTML;
    const current = Number(group.dataset.desiredQuantity ?? group.querySelector('.vc-quantity output').textContent);
    const next = control.hasAttribute('data-cart-remove') ? 0 : Math.max(1, current + Number(control.dataset.cartStep));
    if (!Number.isInteger(next)) return;
    this.pendingChanges.set(id, next);
    this.paintDesired(group, next);
    const error = this.querySelector('[data-cart-error]');
    if (error) error.hidden = true;
    this.setBusy();
    clearTimeout(this.updateTimer);
    this.updateTimer = setTimeout(() => this.flushChanges(), 120);
  }
  async flushChanges() {
    if (this.inFlight || !this.pendingChanges.size) return;
    const batch = new Map(this.pendingChanges);
    this.pendingChanges.clear();
    const scroll = this.querySelector('.vc-items')?.scrollTop || 0;
    this.inFlight = true;
    this.setBusy();
    try {
      const updates = {};
      for (const [id, quantity] of batch) {
        const group = this.groupElement(id);
        const lines = [...(group?.querySelectorAll('[data-cart-line]') || [])];
        if (!lines.length) throw new Error('העגלה השתנתה. רעננו את הדף כדי לעדכן את הכמות.');
        const packages = lines.map(line => Number(line.dataset.lineQuantity) / Number(line.dataset.lineUnits));
        if (packages.some(value => !Number.isInteger(value) || value !== packages[0])) throw new Error('כמות המארז השתנתה מחוץ לעגלה. רעננו את הדף לבדיקה.');
        for (const line of lines) updates[line.dataset.lineKey] = quantity * Number(line.dataset.lineUnits);
      }
      const root = window.Shopify?.routes?.root || '/';
      const state = await this.jsonFetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates, sections: this.getSectionsToRender().map(section => section.id), sections_url: location.pathname }) });
      const focused = document.activeElement;
      const focusedId = focused?.closest('[data-cart-group]')?.dataset.cartGroup;
      const focusedStep = focused?.dataset.cartStep;
      const focusedClose = focused?.matches('[data-cart-close]');
      this.renderContents(state, false);
      this.confirmedMarkup = this.querySelector('#CartDrawer').innerHTML;
      for (const [id, quantity] of this.pendingChanges) {
        const group = this.groupElement(id);
        if (group) this.paintDesired(group, quantity); else this.pendingChanges.delete(id);
      }
      this.querySelector('.vc-items')?.scrollTo(0, scroll);
      if (this.classList.contains('active')) {
        const group = this.groupElement(focusedId);
        const focus = focusedClose ? this.querySelector('[data-cart-close]') : group?.querySelector(focusedStep ? `[data-cart-step="${focusedStep}"]:not(:disabled)` : '[data-cart-step="1"]');
        (focus || this.querySelector('[data-cart-close]'))?.focus({ preventScroll: true });
      }
      const status = this.querySelector('[data-cart-status]');
      if (status) status.textContent = 'הכמות וסכום הביניים עודכנו לפי Shopify.';
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vonela-cart', cartData: state });
    } catch (failure) {
      this.pendingChanges.clear();
      if (this.confirmedMarkup) this.querySelector('#CartDrawer').innerHTML = this.confirmedMarkup;
      this.syncCheckout();
      const error = this.querySelector('[data-cart-error]');
      if (error) { error.textContent = failure.message + ' המחיר אינו משתנה עד לאישור העדכון; רעננו את הדף לבדיקת מצב הסל.'; error.hidden = false; }
      if (this.classList.contains('active')) this.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    } finally {
      this.inFlight = false;
      this.setBusy();
      if (this.pendingChanges.size) this.flushChanges();
    }
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
